import { Injectable } from '@angular/core';
import { isString } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import {
    FilterField,
    SurveyFilterValues
} from 'src/app/feature/map-viewer/map-menus/shared/job-filter/job-filter.types';
import { DateUtils } from 'src/app/shared/common/utility/date-utils';
import { SurveyJobFilterId, SurveyJobStatus } from 'src/app/shared/survey-data-services/survey-job';
import { UserSettingsStreamService } from 'src/app/shared/user/user-settings-stream.service';
import { User } from '../../user/user';
import { UserMapWorkspaceSurveyFilters } from '../../user/user-map-workspace-settings';
import { JobOp } from '../enums/list-panel/job-filter.enum';

export const startDateFields = ['createdFromDate', 'updatedFromDate'];
export const endDateFields = ['createdToDate', 'updatedToDate'];
export const dateGroupIds = [
    [SurveyJobFilterId.createdFromDate, SurveyJobFilterId.createdToDate],
    [SurveyJobFilterId.updatedFromDate, SurveyJobFilterId.updatedToDate]
];
export const fieldMap: { [key: string]: string } = {
    id: '$Id',
    name: '$Name',
    priority: '$Priority',
    status: '$Status',
    tags: '$Tags',
    createdFromDate: '$CreatedUtc',
    createdToDate: '$CreatedUtc',
    updatedFromDate: '$UpdatedUtc',
    updatedToDate: '$UpdatedUtc',
    assigneeUserIds: '$AssigneeUserId',
    createdByUserIds: '$CreatedByUserId'
};
export const statusDisplayNameToValueMap: { [key: string]: SurveyJobStatus } = {
    New: SurveyJobStatus.New,
    'In Progress': SurveyJobStatus.InProgress,
    'Field Work Complete': SurveyJobStatus.FieldWorkComplete,
    Closed: SurveyJobStatus.Closed
};

@Injectable({
    providedIn: 'root'
})
export class JobFilterStreamsService {
    /** Stream to store and access the latest filter expression string */
    public filterExpressionString$ = new BehaviorSubject<string>('');

    constructor(private userSettingsStream: UserSettingsStreamService) {}

    /** Supported filter fields for building expressions */
    private static readonly SUPPORTED_FILTER_FIELDS = [
        'id',
        'name',
        'priority',
        'status',
        'tags',
        'createdFromDate',
        'createdToDate',
        'updatedFromDate',
        'updatedToDate',
        'createdByUserIds',
        'assigneeUserIds'
    ];

    /** Builds a filter expression string from the given filter values */
    public buildFilterExpressionString<T extends { [key: string]: any }>(
        values: T,
        allowedStatus?: SurveyJobStatus[]
    ): string {
        const expressions: string[] = [];

        for (const field of JobFilterStreamsService.SUPPORTED_FILTER_FIELDS) {
            const value = values[field];
            if (field === 'status' && allowedStatus && allowedStatus.length > 0) {
                if (Array.isArray(value) && value.length !== 0) {
                    const allowedSet = new Set(allowedStatus);
                    const filteredAllowed = value.filter(v => allowedSet.has(v));
                    if (filteredAllowed.length === 0) {
                        // User filtered for status not in allowedStatus, so tab should be empty
                        expressions.push(`${fieldMap[field]} ${JobOp.IN} (${SurveyJobStatus.Unknown})`);
                    } else {
                        const statusExpressions = filteredAllowed.map(v => `'${v}'`).join(',');
                        expressions.push(`${fieldMap[field]} ${JobOp.IN} (${statusExpressions})`);
                    }
                } else {
                    const statusExpressions = allowedStatus.map(v => `'${v}'`).join(',');
                    expressions.push(`${fieldMap[field]} ${JobOp.IN} (${statusExpressions})`);
                }
                continue;
            }

            if (!value || (Array.isArray(value) && value.length === 0)) continue;

            if (field === 'tags' && Array.isArray(value)) {
                const tagExpressions = value.map((tag: string) => `${fieldMap[field]} ${JobOp.EQ} '${tag}'`);
                expressions.push(`(${tagExpressions.join(' OR ')})`);
                continue;
            }

            // Array fields (IN)
            if (Array.isArray(value) && value.length > 0) {
                const formatted = value.map(v => `'${v}'`).join(',');
                expressions.push(`${fieldMap[field]} ${JobOp.IN} (${formatted})`);
                continue;
            }

            // Date fields (GE and LE for full day, UTC using DateUtils)
            if (startDateFields.includes(field) && DateUtils.isValidDate(value)) {
                let startDate = isString(value) ? new Date(value) : value;
                expressions.push(`${fieldMap[field]} ${JobOp.GE} '${startDate.toISOString()}'`);
                continue;
            }
            if (endDateFields.includes(field) && DateUtils.isValidDate(value)) {
                let endDate = isString(value) ? new Date(value) : value;
                expressions.push(`${fieldMap[field]} ${JobOp.LE} '${endDate.toISOString()}'`);
                continue;
            }

            // Name field (LIKE for partial match)
            if (field === 'name') {
                expressions.push(`${fieldMap[field]} ${JobOp.CN} '${value}'`);
                continue;
            }
            // String/number fields (EQ)
            expressions.push(`${fieldMap[field]} ${JobOp.EQ} '${value}'`);
        }

        const result = expressions.join(' AND ');
        this.filterExpressionString$.next(result);
        return result;
    }

    /**
     * Returns initial survey filter values from previous filter and project users
     */
    public getInitialSurveyFilterValues(
        previousFilter: UserMapWorkspaceSurveyFilters,
        currentProjectUsers: User[]
    ): SurveyFilterValues {
        const initialValues: SurveyFilterValues = {
            [SurveyJobFilterId.createdUsers]: previousFilter.createdByUserIds
                ? previousFilter.createdByUserIds
                      .map((id: any) => currentProjectUsers.find(u => u.id === id))
                      .filter(Boolean)
                : [],
            [SurveyJobFilterId.assignedUsers]: previousFilter.assigneeUserIds
                ? previousFilter.assigneeUserIds
                      .map((id: any) => currentProjectUsers.find(u => u.id === id))
                      .filter(Boolean)
                : [],
            [SurveyJobFilterId.updatedFromDate]: previousFilter.updatedFromDate
                ? new Date(previousFilter.updatedFromDate)
                : null,
            [SurveyJobFilterId.updatedToDate]: previousFilter.updatedToDate
                ? new Date(previousFilter.updatedToDate)
                : null,
            [SurveyJobFilterId.createdFromDate]: previousFilter.createdFromDate
                ? new Date(previousFilter.createdFromDate)
                : null,
            [SurveyJobFilterId.createdToDate]: previousFilter.createdToDate
                ? new Date(previousFilter.createdToDate)
                : null,
            [SurveyJobFilterId.status]: Array.isArray(previousFilter.status)
                ? previousFilter.status.map((enumValue: string) => {
                      const displayName = Object.keys(statusDisplayNameToValueMap).find(
                          key => statusDisplayNameToValueMap[key] === enumValue
                      );
                      return displayName || enumValue;
                  })
                : [],
            [SurveyJobFilterId.tags]: previousFilter.tags || []
        };
        return initialValues;
    }

    /**
     * Converts survey filter values for user settings
     */
    public convertSurveyFiltersForUserSettings(values: SurveyFilterValues): UserMapWorkspaceSurveyFilters {
        const safeDate = (date: any, fn: (d: any) => any) => {
            if (!date) return null;
            const result = fn(date);
            return DateUtils.isValidDate(result) ? new Date(result).toISOString() : null;
        };
        return {
            status: Array.isArray(values.status)
                ? values.status.map(
                      (displayName: string) => statusDisplayNameToValueMap[displayName].toString() || displayName
                  )
                : [],
            tags: Array.isArray(values.tags) ? values.tags : [],
            createdFromDate: safeDate(values.createdFromDate, d => DateUtils.startOfDay(d, true)),
            createdToDate: safeDate(values.createdToDate, d => DateUtils.endOfDay(d, true)),
            updatedFromDate: safeDate(values.updatedFromDate, d => DateUtils.startOfDay(d, true)),
            updatedToDate: safeDate(values.updatedToDate, d => DateUtils.endOfDay(d, true)),
            assigneeUserIds: Array.isArray(values.assignedUsers) ? values.assignedUsers.map(u => u.id) : [],
            createdByUserIds: Array.isArray(values.createdUsers) ? values.createdUsers.map(u => u.id) : []
        };
    }

    /**
     * Saves survey filters to user settings
     */
    public saveSurveyFiltersToUserSettings(surveyFilters: UserMapWorkspaceSurveyFilters): void {
        const currentWorkspaceSettings = this.userSettingsStream.getCurrentMapWorkspaceSettings();
        if (currentWorkspaceSettings) {
            currentWorkspaceSettings.surveyFilters = surveyFilters;
            this.userSettingsStream.updateCurrentWorkspaceSettings(currentWorkspaceSettings);
        }
    }

    /** Returns the count of applied filters (non-empty values) */
    public static filterAppliedCount<T>(values: T): number {
        return Object.values(values).filter(v => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
            .length;
    }

    public static getDefaultValues(fields: FilterField[]): { [id: string]: any } {
        const values: { [id: string]: any } = {};
        for (const field of fields) {
            if (field.type === 'date') {
                values[field.id] = null;
            } else if (field.type === 'user' || field.type === 'chipFilter') {
                values[field.id] = [];
            } else if (field.type === 'dropdown') {
                values[field.id] = field.multiple ? [] : '';
            }
        }
        return values;
    }
}
